/* eslint-disable @typescript-eslint/no-explicit-any */
import Hls from 'hls.js';
import { useCallback, useEffect, useRef, useState } from 'react';

import api from '../api/api';
import { IotDeviceDetail } from '../types/iot';
import { getAuthCookies } from '../utils/authTokens';

export type CameraStatus =
 | 'idle'
 | 'loading'
 | 'ready'
 | 'playing'
 | 'error';

/**
 * Camera live-stream controller (HLS).
 *
 * On a camera id it loads GET /iot/v1/{id}, builds the HLS url from the device's
 * IotSource (`https://{ServerIP}/{Path}/index.m3u8`) and — on start() — plays it
 * through hls.js, passing the app's current access token as a ?jwt= query
 * param on every request (manifest + segments, via a custom loader).
 */
export const useCameraStream = (cameraId?: number | null) => {
 const [status, setStatus] = useState<CameraStatus>('idle');
 const [name, setName] = useState('');
 const [error, setError] = useState<string | null>(null);
 const [streamUrl, setStreamUrl] = useState('');

 const videoRef = useRef<HTMLVideoElement | null>(null);
 const hlsRef = useRef<Hls | null>(null);
 const urlRef = useRef('');

 const stop = useCallback(() => {
  if (hlsRef.current) {
   try {
    hlsRef.current.stopLoad();
   } catch {
    /* noop */
   }
   try {
    hlsRef.current.detachMedia();
   } catch {
    /* noop */
   }
   try {
    hlsRef.current.destroy();
   } catch {
    /* noop */
   }
   hlsRef.current = null;
  }
  const v = videoRef.current;
  if (v) {
   try {
    v.pause();
   } catch {
    /* noop */
   }
   v.removeAttribute('src');
   try {
    v.load();
   } catch {
    /* noop */
   }
  }
 }, []);

 const start = useCallback(() => {
  const url = urlRef.current;
  const video = videoRef.current;
  if (!url || !video) return;
  stop();

  const token = getAuthCookies().accessToken || '';

  if (Hls.isSupported()) {
   // MediaMTX validates the JWT from the Authorization header (authJWTInHTTPQuery
   // = false). xhrSetup attaches `Bearer <token>` to EVERY request — manifest,
   // playlists, segments, keys.
   const hls = new Hls({
    xhrSetup: (xhr: XMLHttpRequest) => {
     if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    },
    lowLatencyMode: true,
    enableWorker: true,
   });
   hlsRef.current = hls;
   hls.loadSource(url);
   hls.attachMedia(video);
   hls.on(Hls.Events.MANIFEST_PARSED, () => {
    setStatus('playing');
    video.play().catch(() => {
     /* autoplay may be blocked; user can press play */
    });
   });
   hls.on(Hls.Events.ERROR, (_evt, data: any) => {
    if (!data?.fatal) return;
    if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
     setError(`Network: ${data.details}`);
     hls.startLoad();
    } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
     setError(`Media: ${data.details}`);
     hls.recoverMediaError();
    } else {
     setError(`${data.type}: ${data.details}`);
     stop();
     setStatus('error');
    }
   });
  } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
   // Native HLS (Safari): cannot set an Authorization header on <video src>, so
   // fall back to a ?jwt= query param. Requires MediaMTX authJWTInHTTPQuery=true;
   // otherwise Safari playback will 401.
   video.src =
    token && url
     ? `${url}${url.includes('?') ? '&' : '?'}jwt=${encodeURIComponent(token)}`
     : url;
   const onMeta = () => {
    setStatus('playing');
    video.play().catch(() => {
     /* noop */
    });
   };
   video.addEventListener('loadedmetadata', onMeta, { once: true });
   video.addEventListener(
    'error',
    () => {
     setError(`Native HLS error code=${video.error?.code ?? '?'}`);
     setStatus('error');
    },
    { once: true },
   );
  } else {
   setError('HLS not supported in this browser');
   setStatus('error');
  }
 }, [stop]);

 // Stop playback when the tab is hidden (mirrors legacy Watch.cshtml).
 useEffect(() => {
  const onHidden = () => {
   if (document.visibilityState === 'hidden') stop();
  };
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', stop);
  return () => {
   document.removeEventListener('visibilitychange', onHidden);
   window.removeEventListener('pagehide', stop);
  };
 }, [stop]);

 // Load device detail + build stream url whenever the selected camera changes.
 useEffect(() => {
  let cancelled = false;
  stop();
  setStreamUrl('');
  urlRef.current = '';
  setError(null);

  if (!cameraId) {
   setStatus('idle');
   setName('');
   return undefined;
  }

  const run = async () => {
   try {
    setStatus('loading');
    const { data } = await api.get(`/iot/v1/${cameraId}`);
    if (cancelled) return;
    const detail = data as IotDeviceDetail;
    setName(detail?.Name ?? '');

    const src = detail?.IotSource;
    const host = String(src?.ServerIP ?? '')
     .replace(/^https?:\/\//i, '')
     .replace(/\/+$/, '');
    const path = String(detail?.Path ?? '').replace(/^\/+|\/+$/g, '');
    if (!host || !path) {
     setStatus('error');
     setError('Missing camera server / path');
     return;
    }
    // Encode each path segment but keep the slashes (matches MediaMTX path layout).
    const encodedPath = path.split('/').map(encodeURIComponent).join('/');
    const url = `https://${host}/${encodedPath}/index.m3u8`;
    urlRef.current = url;
    setStreamUrl(url);
    setStatus('ready');
    // Auto-start playback as soon as the camera is selected.
    start();
   } catch (e: any) {
    if (cancelled) return;
    setStatus('error');
    setError(e?.message ?? 'Failed to load camera');
   }
  };

  run();

  return () => {
   cancelled = true;
   stop();
  };
 }, [cameraId, stop, start]);

 return { status, name, error, streamUrl, videoRef, start, stop };
};

export default useCameraStream;
